import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layers, Sparkles, Save, ArrowLeft, ArrowRight, AlertTriangle,
  CheckCircle, Info, XCircle, Plus, Trash2, Loader, BookOpen, ArrowUp, ArrowDown,
  Wand2, Target, ChevronRight, Copy,
} from 'lucide-react';
import CopierVersOptionsModal from './CopierVersOptionsModal';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { useGetOptionsQuery } from '../../services/polesOptionsApi';
import {
  useLazyGetPropositionRepartitionQuery,
  useAppliquerRepartitionMutation,
  useLazyDetecterMutualisationsQuery,
  PropositionUE,
  PropositionMatiere,
  PropositionAlerte,
  GroupeMutualisable,
} from '../../services/uesApi';
import { useRecalculerCSMutation } from '../../services/resultatsApi';

// ─── Types UI ───────────────────────────────────────────────────────────────

interface UELocal extends Omit<PropositionUE, 'matieres'> {
  matieres: PropositionMatiere[];
  _tempId: string; // pour tracking React
}

// ─── Algorithme de suggestion d'UE ──────────────────────────────────────────
// Score chaque UE pour une matière donnée selon plusieurs heuristiques :
//   - Catégorie identique (poids fort)
//   - Mots-clés communs entre nom matière ↔ nom UE / matières existantes
//   - TP vs non-TP (compatibilité)
//   - Charge actuelle (évite UE saturée, préfère UE incomplète)
// Renvoie les 1-3 meilleures UE avec score et raisons.

interface Suggestion {
  ueIdx: number;
  score: number;
  raisons: string[];
}

const STOP_WORDS = new Set(['de', 'du', 'des', 'la', 'le', 'les', 'et', 'aux', 'aux', 'pour', 'sur']);

function tokens(s: string): string[] {
  return s.toLowerCase()
    .replace(/[àâä]/g, 'a').replace(/[éèêë]/g, 'e').replace(/[îï]/g, 'i')
    .replace(/[ôö]/g, 'o').replace(/[ùûü]/g, 'u').replace(/ç/g, 'c')
    .split(/[\s,()\-_/.;:]+/)
    .filter(w => w.length >= 4 && !STOP_WORDS.has(w));
}

function dominantType(ue: UELocal): string | null {
  const count: Record<string, number> = {};
  for (const m of ue.matieres) {
    const t = (m as any).type_enseignement;
    if (t) count[t] = (count[t] || 0) + 1;
  }
  const entries = Object.entries(count);
  if (entries.length === 0) return null;
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0];
}

function suggererUEs(matiere: PropositionMatiere, ues: UELocal[]): Suggestion[] {
  const matWords = tokens(matiere.nom_matiere);
  const isTPMatiere = (matiere.categorie || '').toLowerCase().includes('tp')
    || matiere.nom_matiere.toLowerCase().includes('tp ')
    || matiere.nom_matiere.toLowerCase().startsWith('tp');
  const matType = (matiere as any).type_enseignement as string | undefined;

  const scored = ues.map((ue, ueIdx): Suggestion => {
    let score = 0;
    const raisons: string[] = [];

    // 1) Catégorie identique (thématique)
    const sameCat = ue.matieres.filter(m => m.categorie === matiere.categorie).length;
    if (sameCat > 0 && matiere.categorie) {
      score += sameCat * 10;
      raisons.push(`${sameCat} matière${sameCat > 1 ? 's' : ''} « ${matiere.categorie} »`);
    }

    // 2) Type d'enseignement cohérent (fondamentale/pro/transversale)
    if (matType) {
      const ueDomType = dominantType(ue);
      if (ueDomType && ueDomType === matType) {
        const sameType = ue.matieres.filter(m => (m as any).type_enseignement === matType).length;
        score += 8 + Math.min(sameType * 3, 15);
        raisons.push(`type ${matType} (${sameType} similaires)`);
      } else if (ueDomType && ueDomType !== matType) {
        // Mélanger fondamentale/pro/transversale est déconseillé
        score -= 6;
        raisons.push(`type UE : ${ueDomType} ≠ ${matType}`);
      }
    }

    // 3) Mots-clés du nom UE communs avec le nom matière
    const ueWords = tokens(ue.nom_ue);
    const commonName = matWords.filter(w =>
      ueWords.some(uw => uw === w || uw.startsWith(w) || w.startsWith(uw))
    );
    if (commonName.length > 0) {
      score += commonName.length * 15;
      raisons.push(`mot-clé : ${commonName.slice(0, 2).join(', ')}`);
    }

    // 4) Affinité avec les noms des matières déjà dans l'UE
    let nameOverlap = 0;
    for (const m of ue.matieres) {
      const mWords = tokens(m.nom_matiere);
      for (const w of matWords) {
        if (mWords.some(mw => mw === w || mw.startsWith(w) || w.startsWith(mw))) {
          nameOverlap++;
        }
      }
    }
    if (nameOverlap > 0) {
      score += Math.min(nameOverlap * 5, 25);
      raisons.push(`affinité noms (${nameOverlap})`);
    }

    // 5) Cohérence TP / non-TP
    if (ue.est_tp && !isTPMatiere) {
      score -= 30;
    } else if (!ue.est_tp && isTPMatiere) {
      score -= 15;
    } else if (ue.est_tp && isTPMatiere) {
      score += 8;
      raisons.push('UE TP compatible');
    }

    // 6) Charge actuelle (évite saturation, préfère UE incomplète)
    const currentCr = ue.matieres.reduce((s, m) => s + m.credits, 0);
    if (currentCr + matiere.credits > 12) {
      score -= 8;
      raisons.push(`déjà ${currentCr}cr`);
    } else if (currentCr === 0) {
      score -= 3;
    } else if (currentCr < 6) {
      score += 2;
    }

    return { ueIdx, score, raisons };
  });

  return scored
    .filter(s => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);
}

// Couleur associée au type d'enseignement
function typeBadgeClass(type?: string): string {
  switch (type) {
    case 'transversale':
      return 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300';
    case 'fondamentale':
      return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300';
    case 'professionnelle':
      return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
    default:
      return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  }
}

// ─── Composant principal ────────────────────────────────────────────────────

// Semestres (absolus) disponibles par niveau. Les Masters utilisent les
// semestres 7-10 (M1 → S7/S8, M2 → S9/S10) ; sans ça, le sélecteur ne
// proposait que S1-S6 et la répartition UE des Masters était inaccessible.
const SEMESTRES_PAR_NIVEAU: Record<string, number[]> = {
  L1: [1, 2], L2: [3, 4], L3: [5, 6], M1: [7, 8], M2: [9, 10],
};

const RepartitionUE: React.FC = () => {
  const navigate = useNavigate();
  const [niveau, setNiveau] = useState<string>('L1');
  const [semestre, setSemestre] = useState<number>(1);
  const [optionId, setOptionId] = useState<number | null>(null);
  // Fix 2026-09-10 : filtres pôle / parcours / FI-CS pour affiner la liste d'options
  const [filtrePole, setFiltrePole] = useState<string>('');
  const [filtreParcours, setFiltreParcours] = useState<string>('');
  const [filtreTypeFormation, setFiltreTypeFormation] = useState<'' | 'FI' | 'CS'>('');

  const [ues, setUEs] = useState<UELocal[]>([]);
  const [nonClassees, setNonClassees] = useState<PropositionMatiere[]>([]);
  const [alertes, setAlertes] = useState<PropositionAlerte[]>([]);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Sélection active : {ueIndex, matiereId}
  const [selection, setSelection] = useState<{ ueIdx: number | 'orphans'; mid: number } | null>(null);
  const [showCopierModal, setShowCopierModal] = useState(false);

  const { data: options = [] } = useGetOptionsQuery({ actif: true });
  const [fetchProposition, { isFetching }] = useLazyGetPropositionRepartitionQuery();
  const [appliquer, { isLoading: isSaving }] = useAppliquerRepartitionMutation();
  const [recalculerCS, { isLoading: isRecalculatingCS }] = useRecalculerCSMutation();
  // Popup post-save proposant de relancer le recalcul CS (uniquement pour options CS)
  const [recalcCSPropose, setRecalcCSPropose] = useState<null | {
    optionNom: string;
    matieresDeplacees: number;
  }>(null);
  const [fetchMutualisations, { data: mutuData }] = useLazyDetecterMutualisationsQuery();

  const selectedOption = options.find(o => o.id === optionId);

  // Fix 2026-09-10 : listes uniques pôle / parcours à partir des options
  const polesDispo = useMemo(() => {
    const s = new Set<string>();
    options.forEach(o => { if ((o as any).pole_nom) s.add((o as any).pole_nom); });
    return Array.from(s).sort();
  }, [options]);
  const parcoursDispo = useMemo(() => {
    const s = new Set<string>();
    options.forEach(o => { if ((o as any).parcours_nom) s.add((o as any).parcours_nom); });
    return Array.from(s).sort();
  }, [options]);

  // Options filtrées pour le sélecteur principal
  const optionsFiltrees = useMemo(() => {
    let list = [...options];
    if (filtrePole) list = list.filter(o => (o as any).pole_nom === filtrePole);
    if (filtreParcours) list = list.filter(o => ((o as any).parcours_nom || '') === filtreParcours);
    if (filtreTypeFormation === 'CS') {
      list = list.filter(o => o.nom.includes('(CS)') || (o.code || '').endsWith('-CS'));
    } else if (filtreTypeFormation === 'FI') {
      list = list.filter(o => !o.nom.includes('(CS)') && !(o.code || '').endsWith('-CS'));
    }
    return list;
  }, [options, filtrePole, filtreParcours, filtreTypeFormation]);

  // Reset option sélectionnée si elle n'est plus dans la liste filtrée
  useEffect(() => {
    if (optionId && !optionsFiltrees.some(o => o.id === optionId)) {
      setOptionId(null);
    }
  }, [optionsFiltrees, optionId]);

  // Stats temps réel
  const stats = useMemo(() => {
    const totalCredits = ues.reduce((s, ue) => s + ue.matieres.reduce((ss, m) => ss + m.credits, 0), 0);
    const totalMatieres = ues.reduce((s, ue) => s + ue.matieres.length, 0) + nonClassees.length;
    const creditsParUE = ues.map(ue => ({
      nom: ue.nom_ue,
      credits: ue.matieres.reduce((s, m) => s + m.credits, 0),
      nb: ue.matieres.length,
      est_tp: ue.est_tp,
    }));
    return { totalCredits: Math.round(totalCredits * 100) / 100, totalMatieres, creditsParUE };
  }, [ues, nonClassees]);

  // Alertes temps réel
  const alertesLive = useMemo((): PropositionAlerte[] => {
    const live: PropositionAlerte[] = [];
    if (Math.abs(stats.totalCredits - 30) < 0.01) {
      live.push({ type: 'success', message: `Total : ${stats.totalCredits} crédits (conforme LMD)` });
    } else if (stats.totalCredits < 28) {
      live.push({ type: 'warning', message: `Total insuffisant : ${stats.totalCredits} crédits (cible : 30)` });
    } else if (stats.totalCredits > 32) {
      live.push({ type: 'warning', message: `Total trop élevé : ${stats.totalCredits} crédits (cible : 30)` });
    } else {
      live.push({ type: 'info', message: `Total : ${stats.totalCredits} crédits (proche de 30)` });
    }
    if (nonClassees.length > 0) {
      live.push({ type: 'error', message: `${nonClassees.length} matière(s) non affectée(s). Glissez-les dans une UE.` });
    }
    const uesNonTp = ues.filter(ue => !ue.est_tp && ue.matieres.length > 0);
    if (uesNonTp.length >= 2) {
      const credits = uesNonTp.map(ue => ue.matieres.reduce((s, m) => s + m.credits, 0));
      const min = Math.min(...credits);
      const max = Math.max(...credits);
      if (max - min > 5) {
        live.push({ type: 'warning', message: `UE déséquilibrées : min ${min} / max ${max} crédits (écart > 5)` });
      }
    }
    ues.forEach(ue => {
      if (!ue.est_tp && ue.matieres.length > 5) {
        live.push({ type: 'warning', message: `'${ue.nom_ue}' contient ${ue.matieres.length} matières (>5)` });
      }
      if (!ue.est_tp && ue.matieres.length === 1) {
        live.push({ type: 'info', message: `'${ue.nom_ue}' ne contient qu'1 matière` });
      }
    });
    return live;
  }, [ues, nonClassees, stats.totalCredits]);

  // Charger la répartition depuis l'API
  // mode 'auto' (défaut) : charge la structure DB si existe, sinon génère
  // mode 'propose' : force la régénération d'une nouvelle proposition
  const handleGenererProposition = async (mode: 'auto' | 'propose' = 'auto') => {
    if (!optionId) {
      setMessage({ type: 'error', text: 'Sélectionnez une option avant de générer.' });
      return;
    }
    setMessage(null);
    try {
      const result = await fetchProposition({ niveau, semestre, option_id: optionId, mode }).unwrap();
      const uesLocal: UELocal[] = result.ues.map((ue, i) => ({
        ...ue,
        _tempId: `ue-${i}-${Date.now()}`,
      }));
      setUEs(uesLocal);
      setNonClassees(result.non_classees);
      setAlertes(result.alertes);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.data?.error || 'Erreur de chargement' });
    }
  };

  // Auto-charger au changement de sélection : mode auto (DB en priorité)
  useEffect(() => {
    if (optionId) handleGenererProposition('auto');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [niveau, semestre, optionId]);

  // Détection des mutualisations possibles au changement niveau/semestre
  useEffect(() => {
    fetchMutualisations({ niveau, semestre, seuil: 0.6 });
  }, [niveau, semestre, fetchMutualisations]);

  // Le groupe mutualisable qui contient l'option actuellement sélectionnée
  const groupeMutuActuel: GroupeMutualisable | null = useMemo(() => {
    if (!mutuData || !optionId) return null;
    return mutuData.groupes_mutualisables.find(g =>
      g.options.some(o => o.id === optionId)
    ) || null;
  }, [mutuData, optionId]);

  // Déplacer une matière d'une UE à une autre
  const moveMatiere = (matiereId: number, fromIdx: number | 'orphans', toIdx: number | 'orphans') => {
    if (fromIdx === toIdx) return;
    setUEs(prev => {
      const next = prev.map(ue => ({ ...ue, matieres: [...ue.matieres] }));
      let matiere: PropositionMatiere | null = null;
      if (fromIdx === 'orphans') {
        matiere = nonClassees.find(m => m.id === matiereId) || null;
        if (matiere) setNonClassees(prev => prev.filter(m => m.id !== matiereId));
      } else {
        const src = next[fromIdx];
        matiere = src.matieres.find(m => m.id === matiereId) || null;
        src.matieres = src.matieres.filter(m => m.id !== matiereId);
      }
      if (!matiere) return prev;
      if (toIdx === 'orphans') {
        setNonClassees(prev => [...prev, matiere!]);
      } else {
        next[toIdx].matieres = [...next[toIdx].matieres, matiere];
      }
      return next;
    });
    setSelection(null);
  };

  // Ajouter une UE vide
  const addUE = () => {
    setUEs(prev => [...prev, {
      ordre: prev.length + 1,
      code_ue: `UE-${niveau}S${semestre}-NEW${prev.length + 1}-${selectedOption?.code || 'XX'}`,
      nom_ue: `Nouvelle UE ${prev.length + 1}`,
      est_tp: false,
      categories: [],
      matieres: [],
      credits_total: 0,
      nb_matieres: 0,
      _tempId: `ue-new-${Date.now()}`,
    }]);
  };

  // Supprimer une UE (renvoie ses matières aux orphelines)
  // Fix 2026-09-10 : réordonner les UE (monter / descendre)
  const moveUE = (idx: number, direction: -1 | 1) => {
    setUEs(prev => {
      const target = idx + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
    setSelection(null);
  };

  const removeUE = (idx: number) => {
    const ue = ues[idx];
    if (!ue) return;
    if (ue.matieres.length > 0) {
      setNonClassees(prev => [...prev, ...ue.matieres]);
    }
    setUEs(prev => prev.filter((_, i) => i !== idx));
  };

  const updateUEField = (idx: number, field: 'nom_ue' | 'code_ue', value: string) => {
    setUEs(prev => prev.map((ue, i) => i === idx ? { ...ue, [field]: value } : ue));
  };

  const toggleTP = (idx: number) => {
    setUEs(prev => prev.map((ue, i) => i === idx ? { ...ue, est_tp: !ue.est_tp } : ue));
  };

  // Sauvegarder la répartition
  const handleSauver = async () => {
    if (!optionId) return;
    if (nonClassees.length > 0) {
      if (!window.confirm(`${nonClassees.length} matière(s) non affectée(s). Continuer ?`)) return;
    }
    const payload = {
      niveau,
      semestre,
      option_id: optionId,
      ues: ues.map((ue, i) => ({
        code_ue: ue.code_ue,
        nom_ue: ue.nom_ue,
        ordre_affichage: i + 1,
        matiere_ids: ue.matieres.map(m => m.id),
      })),
    };
    try {
      const result = await appliquer(payload).unwrap();
      setMessage({ type: 'success', text: result.message });
      // Recharge la structure depuis la BDD pour confirmer visuellement que
      // les modifications sont bien persistées (au cas où l'utilisateur
      // douterait de la sauvegarde).
      await handleGenererProposition('auto');
      // Si l'option est CS et que des matières ont bougé, proposer le recalcul
      // des moyennes CS (sinon le Tableau Délibération reste sur d'anciennes
      // moyennes basées sur l'ancienne structure UE).
      if (result.recalcul_cs_recommande && (result.delta?.matieres_deplacees ?? 0) > 0) {
        setRecalcCSPropose({
          optionNom: result.option_nom || selectedOption?.nom || '',
          matieresDeplacees: result.delta?.matieres_deplacees ?? 0,
        });
      }
    } catch (err: any) {
      // Fix 2026-09-09 : afficher le détail complet du message backend
      const detail = err?.data?.error
        || err?.data?.detail
        || err?.data?.message
        || (typeof err?.data === 'string' ? err.data : '')
        || err?.error
        || `Erreur sauvegarde (HTTP ${err?.status || '?'})`;
      setMessage({ type: 'error', text: detail });
      // eslint-disable-next-line no-console
      console.error('appliquer_repartition erreur', err);
    }
  };

  // Lance le recalcul CS depuis la modale (option filtrée).
  const handleRecalculerCSFromModal = async () => {
    if (!recalcCSPropose || !selectedOption) return;
    try {
      const stats = await recalculerCS({
        semestre: (((semestre - 1) % 2) + 1) as 1 | 2,
        niveau,
        option: recalcCSPropose.optionNom,
      }).unwrap();
      setMessage({
        type: 'success',
        text: `Recalcul CS terminé : ${stats.total_etudiants_cs} étudiants traités, ${stats.semestres_calcules} semestres recalculés, ${stats.classes_touchees.length} classe(s) touchée(s).`,
      });
      setRecalcCSPropose(null);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.data?.error || 'Erreur recalcul CS' });
    }
  };

  const alerteIcon = (type: PropositionAlerte['type']) => {
    if (type === 'success') return <CheckCircle size={14} className="text-green-600" />;
    if (type === 'warning') return <AlertTriangle size={14} className="text-amber-600" />;
    if (type === 'error') return <XCircle size={14} className="text-red-600" />;
    return <Info size={14} className="text-blue-600" />;
  };

  const alerteBg = (type: PropositionAlerte['type']) => {
    if (type === 'success') return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 text-green-800 dark:text-green-300';
    if (type === 'warning') return 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-300';
    if (type === 'error') return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 text-red-800 dark:text-red-300';
    return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 text-blue-800 dark:text-blue-300';
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Layers size={28} className="text-blue-600" />
            Répartition des matières en UE (LMD)
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
            Générez une proposition automatique puis ajustez la répartition manuellement.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            icon={<Sparkles size={16} />}
            onClick={() => {
              if (ues.length > 0 && !window.confirm('Régénérer une nouvelle proposition écrasera l\'affichage actuel (mais ne supprime rien en base tant que vous ne sauvez pas). Continuer ?')) return;
              handleGenererProposition('propose');
            }}
            disabled={!optionId || isFetching}
            title="Force la regénération depuis l'algorithme LMD (ignore la structure sauvegardée)"
          >
            Regénérer proposition
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<Save size={16} />}
            onClick={handleSauver}
            disabled={!optionId || ues.length === 0 || isSaving}
          >
            {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon={<Copy size={16} />}
            onClick={() => setShowCopierModal(true)}
            disabled={!optionId || ues.length === 0}
            title="Copier la structure UE de cette option vers d'autres options (sans toucher aux notes)"
          >
            Copier vers d'autres options
          </Button>
        </div>
      </div>

      {message && (
        <div className={`p-3 rounded-lg border ${alerteBg(message.type === 'success' ? 'success' : 'error')} flex items-center gap-2`}>
          {alerteIcon(message.type === 'success' ? 'success' : 'error')}
          <span className="text-sm">{message.text}</span>
        </div>
      )}

      {/* Sélecteurs */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Niveau</label>
            <select
              value={niveau}
              onChange={e => {
                const n = e.target.value;
                setNiveau(n);
                const sems = SEMESTRES_PAR_NIVEAU[n] || [1, 2, 3, 4, 5, 6];
                if (!sems.includes(semestre)) setSemestre(sems[0]);
              }}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              {['L1', 'L2', 'L3', 'M1', 'M2'].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Semestre</label>
            <select
              value={semestre}
              onChange={e => setSemestre(Number(e.target.value))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              {(SEMESTRES_PAR_NIVEAU[niveau] || [1, 2, 3, 4, 5, 6]).map(s => <option key={s} value={s}>S{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Option * <span className="text-gray-400">({optionsFiltrees.length}/{options.length})</span>
            </label>
            <select
              value={optionId ?? ''}
              onChange={e => setOptionId(e.target.value ? Number(e.target.value) : null)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">-- Choisir --</option>
              {optionsFiltrees.map(o => (
                <option key={o.id} value={o.id}>
                  [{(o as any).pole_nom?.substring(0, 4) || '?'}] {o.code} — {o.nom}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Fix 2026-09-10 : ligne de filtres pour restreindre la liste des options */}
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] uppercase font-semibold text-gray-500 dark:text-gray-400 mb-1">
              Pôle
            </label>
            <select
              value={filtrePole}
              onChange={e => setFiltrePole(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Tous pôles</option>
              {polesDispo.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase font-semibold text-gray-500 dark:text-gray-400 mb-1">
              Parcours
            </label>
            <select
              value={filtreParcours}
              onChange={e => setFiltreParcours(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Tous parcours</option>
              {parcoursDispo.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase font-semibold text-gray-500 dark:text-gray-400 mb-1">
              Type de formation
            </label>
            <div className="flex gap-1">
              <select
                value={filtreTypeFormation}
                onChange={e => setFiltreTypeFormation(e.target.value as any)}
                className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">FI + CS</option>
                <option value="FI">Formation Initiale (FI)</option>
                <option value="CS">Certificat Supplémentaire (CS)</option>
              </select>
              {(filtrePole || filtreParcours || filtreTypeFormation) && (
                <button
                  type="button"
                  onClick={() => { setFiltrePole(''); setFiltreParcours(''); setFiltreTypeFormation(''); }}
                  title="Réinitialiser les filtres"
                  className="px-2 text-xs text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded border border-amber-300 dark:border-amber-700"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Stats temps réel */}
        {ues.length > 0 && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
              <div className="text-xs text-blue-600 dark:text-blue-400">Total matières</div>
              <div className="text-xl font-bold text-blue-700 dark:text-blue-300">{stats.totalMatieres}</div>
            </div>
            <div className={`p-3 rounded-lg border ${
              Math.abs(stats.totalCredits - 30) < 0.01
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700'
            }`}>
              <div className={`text-xs ${Math.abs(stats.totalCredits - 30) < 0.01 ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                Total crédits (cible 30)
              </div>
              <div className={`text-xl font-bold ${Math.abs(stats.totalCredits - 30) < 0.01 ? 'text-green-700 dark:text-green-300' : 'text-amber-700 dark:text-amber-300'}`}>
                {stats.totalCredits}
              </div>
            </div>
            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
              <div className="text-xs text-purple-600 dark:text-purple-400">Nombre d'UE</div>
              <div className="text-xl font-bold text-purple-700 dark:text-purple-300">{ues.length}</div>
            </div>
            <div className={`p-3 rounded-lg border ${nonClassees.length > 0 ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700' : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'}`}>
              <div className={`text-xs ${nonClassees.length > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500'}`}>
                Non affectées
              </div>
              <div className={`text-xl font-bold ${nonClassees.length > 0 ? 'text-red-700 dark:text-red-300' : 'text-gray-600'}`}>
                {nonClassees.length}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Bannière mutualisation détectée */}
      {groupeMutuActuel && (
        <Card className="border-purple-300 dark:border-purple-700 bg-purple-50/60 dark:bg-purple-900/15">
          <div className="flex items-start gap-3 flex-wrap">
            <Sparkles size={22} className="text-purple-600 dark:text-purple-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-[260px]">
              <div className="text-sm font-semibold text-purple-800 dark:text-purple-200 mb-0.5">
                {groupeMutuActuel.options.length} options partagent {groupeMutuActuel.nb_matieres_communes} matière{groupeMutuActuel.nb_matieres_communes > 1 ? 's' : ''} identique{groupeMutuActuel.nb_matieres_communes > 1 ? 's' : ''}
              </div>
              <div className="text-xs text-purple-700 dark:text-purple-300 mb-2">
                Similarité : <strong>{(groupeMutuActuel.score_similarite * 100).toFixed(0)}%</strong>
                {' · '}
                {groupeMutuActuel.recommandation === 'mutualisation_forte'
                  ? 'Mutualisation fortement recommandée'
                  : 'Mutualisation partielle possible'}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {groupeMutuActuel.options.map(o => (
                  <span
                    key={o.id}
                    className={`text-[11px] px-2 py-0.5 rounded border ${
                      o.id === optionId
                        ? 'bg-purple-600 text-white border-purple-700 font-semibold'
                        : 'bg-white dark:bg-gray-800 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700'
                    }`}
                  >
                    {o.code} — {o.nom} ({o.nb_matieres} mat., {o.nb_specifiques ?? 0} spéc.)
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-1.5 shrink-0">
              <Button
                variant="primary"
                size="sm"
                icon={<Layers size={14} />}
                onClick={() => {
                  const ids = groupeMutuActuel.options.map(o => o.id).join(',');
                  navigate(`/repartition-ue/mutualisee?niveau=${niveau}&semestre=${semestre}&options_ids=${ids}`);
                }}
                title="Traiter toutes ces options ensemble (tronc commun + spécifiques)"
              >
                Vue mutualisée
              </Button>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400 italic">
            Astuce : appliquer la même répartition sur ces options évite de répéter le travail.
            Tu peux aussi utiliser « Copier vers option » dans Gestion Matières une fois la 1ère faite.
          </p>
        </Card>
      )}

      {/* Alertes & conseils */}
      {alertesLive.length > 0 && (
        <div className="space-y-2">
          {alertesLive.map((a, i) => (
            <div key={i} className={`p-2.5 rounded-lg border ${alerteBg(a.type)} flex items-center gap-2 text-sm`}>
              {alerteIcon(a.type)}
              <span>{a.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Loading */}
      {isFetching && (
        <div className="flex items-center justify-center py-16">
          <Spinner size="lg" />
        </div>
      )}

      {/* Pas d'option */}
      {!optionId && !isFetching && (
        <Card>
          <div className="text-center py-12 text-gray-400">
            <BookOpen size={48} className="mx-auto mb-3 opacity-30" />
            <p className="font-medium text-gray-500">Sélectionnez une option pour voir la proposition</p>
          </div>
        </Card>
      )}

      {/* Matières non classées avec suggestions intelligentes */}
      {optionId && !isFetching && nonClassees.length > 0 && (
        <Card className="border-red-300 dark:border-red-700 bg-red-50/50 dark:bg-red-900/10">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-sm font-semibold text-red-700 dark:text-red-300 flex items-center gap-1">
              <AlertTriangle size={16} />
              Matières non affectées ({nonClassees.length})
            </h3>
            <Button
              variant="primary"
              size="sm"
              icon={<Wand2 size={14} />}
              onClick={() => {
                // Affecter chaque non-classée à sa meilleure UE
                let affectees = 0;
                let restantes = [...nonClassees];
                let curUEs = ues.map(ue => ({ ...ue, matieres: [...ue.matieres] }));
                const orphelinsRestants: PropositionMatiere[] = [];

                for (const m of nonClassees) {
                  const sugg = suggererUEs(m, curUEs);
                  if (sugg.length > 0 && sugg[0].score >= 5) {
                    curUEs[sugg[0].ueIdx].matieres = [...curUEs[sugg[0].ueIdx].matieres, m];
                    affectees++;
                  } else {
                    orphelinsRestants.push(m);
                  }
                }
                setUEs(curUEs);
                setNonClassees(orphelinsRestants);
                setMessage({
                  type: affectees === nonClassees.length ? 'success' : 'error',
                  text: `${affectees}/${nonClassees.length} matière(s) affectée(s) automatiquement`
                    + (orphelinsRestants.length > 0
                      ? ` — ${orphelinsRestants.length} sans suggestion fiable, à placer manuellement.`
                      : '')
                });
              }}
              disabled={ues.length === 0}
            >
              Affecter toutes automatiquement ({nonClassees.length})
            </Button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Pour chaque matière, les UE les plus probables sont proposées. Cliquez sur une suggestion pour déplacer en 1 clic.
          </p>

          <div className="space-y-2">
            {nonClassees.map(m => {
              const suggestions = suggererUEs(m, ues);
              const isSelected = selection?.mid === m.id && selection?.ueIdx === 'orphans';
              return (
                <div
                  key={m.id}
                  className={`p-2.5 rounded-lg border transition-colors ${
                    isSelected
                      ? 'bg-red-100 dark:bg-red-900/30 border-red-400 dark:border-red-600'
                      : 'bg-white dark:bg-gray-800 border-red-200 dark:border-red-700/50'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <button
                      onClick={() => setSelection({ ueIdx: 'orphans', mid: m.id })}
                      className="flex items-center gap-2 text-left"
                    >
                      <span className="font-medium text-sm text-gray-800 dark:text-gray-200">{m.nom_matiere}</span>
                      <span className="text-[10px] text-gray-500 font-mono">{m.code_matiere}</span>
                      <span className="text-xs text-blue-700 dark:text-blue-300 font-semibold">{m.credits} cr</span>
                      {m.categorie && (
                        <span className="text-[9px] uppercase px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                          {m.categorie}
                        </span>
                      )}
                      {(m as any).type_enseignement && (
                        <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded font-medium ${typeBadgeClass((m as any).type_enseignement)}`}>
                          {(m as any).type_enseignement?.substring(0, 5)}
                        </span>
                      )}
                    </button>
                    {suggestions.length === 0 && (
                      <span className="text-[11px] text-gray-400 italic">Aucune suggestion automatique</span>
                    )}
                  </div>
                  {suggestions.length > 0 && (
                    <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                      <Target size={11} className="text-blue-500" />
                      <span className="text-[11px] text-gray-500 mr-1">Proposé →</span>
                      {suggestions.map((s, i) => {
                        const ue = ues[s.ueIdx];
                        const isTop = i === 0;
                        return (
                          <button
                            key={s.ueIdx}
                            onClick={() => moveMatiere(m.id, 'orphans', s.ueIdx)}
                            title={s.raisons.join(' · ') || 'suggestion'}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium border transition-colors ${
                              isTop
                                ? 'bg-blue-600 text-white border-blue-700 hover:bg-blue-700'
                                : 'bg-white dark:bg-gray-800 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700/50 hover:bg-blue-50 dark:hover:bg-blue-900/30'
                            }`}
                          >
                            <ChevronRight size={10} />
                            <span className="font-mono opacity-80">{ue.code_ue.split('-').slice(-2, -1)[0] || `UE${s.ueIdx + 1}`}</span>
                            <span>{ue.nom_ue.length > 22 ? ue.nom_ue.substring(0, 20) + '…' : ue.nom_ue}</span>
                            <span className="opacity-60 text-[9px]">[{s.score}]</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* UEs */}
      {optionId && !isFetching && ues.length > 0 && (
        <div className="space-y-3">
          {ues.map((ue, idx) => (
            <Card key={ue._tempId} className={ue.est_tp ? 'border-orange-300 dark:border-orange-700 bg-orange-50/30 dark:bg-orange-900/10' : ''}>
              {/* Header UE */}
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="flex-1 min-w-[300px]">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="px-2 py-0.5 bg-blue-600 text-white rounded text-xs font-medium">
                      UE {idx + 1}
                    </span>
                    <input
                      type="text"
                      value={ue.code_ue}
                      onChange={e => updateUEField(idx, 'code_ue', e.target.value)}
                      className="px-2 py-1 text-xs font-mono border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                    />
                    {(() => {
                      const t = dominantType(ue);
                      return t ? (
                        <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded font-bold ${typeBadgeClass(t)}`}
                              title={`Type dominant : ${t}`}>
                          {t}
                        </span>
                      ) : null;
                    })()}
                    <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={ue.est_tp}
                        onChange={() => toggleTP(idx)}
                        className="rounded"
                      />
                      UE Travaux Pratiques
                    </label>
                  </div>
                  <input
                    type="text"
                    value={ue.nom_ue}
                    onChange={e => updateUEField(idx, 'nom_ue', e.target.value)}
                    className="w-full px-2 py-1.5 text-sm font-semibold text-gray-800 dark:text-gray-200 bg-transparent border-b border-gray-200 dark:border-gray-600 focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div className="flex items-center gap-2">
                  {(() => {
                    const cr = ue.matieres.reduce((s, m) => s + m.credits, 0);
                    const ok = cr >= 5 && cr <= 10;
                    const empty = cr === 0;
                    const cls = empty
                      ? 'text-gray-400'
                      : ok
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-amber-600 dark:text-amber-400';
                    return (
                      <div className="text-right" title={ok ? 'Crédits équilibrés (5-10)' : empty ? 'UE vide' : 'Hors fourchette idéale (5-10 cr)'}>
                        <div className="text-xs text-gray-500 flex items-center justify-end gap-1">
                          Crédits
                          {ok && !empty && <CheckCircle size={10} className="text-green-500" />}
                          {!ok && !empty && <AlertTriangle size={10} className="text-amber-500" />}
                        </div>
                        <div className={`text-xl font-bold transition-colors ${cls}`}>
                          {cr.toFixed(1)}
                        </div>
                      </div>
                    );
                  })()}
                  <div className="text-right border-l border-gray-200 dark:border-gray-600 pl-3">
                    <div className="text-xs text-gray-500">Matières</div>
                    <div className="text-xl font-bold text-purple-700 dark:text-purple-300">{ue.matieres.length}</div>
                  </div>
                  {selection && selection.ueIdx !== idx && (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<ArrowRight size={14} />}
                      onClick={() => moveMatiere(selection.mid, selection.ueIdx, idx)}
                    >
                      Déplacer ici
                    </Button>
                  )}
                  {/* Fix 2026-09-10 : boutons monter / descendre */}
                  <div className="flex flex-col gap-0.5 border-l border-gray-200 dark:border-gray-600 pl-2">
                    <button
                      onClick={() => moveUE(idx, -1)}
                      disabled={idx === 0}
                      className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Monter cette UE"
                    >
                      <ArrowUp size={12} />
                    </button>
                    <button
                      onClick={() => moveUE(idx, 1)}
                      disabled={idx === ues.length - 1}
                      className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Descendre cette UE"
                    >
                      <ArrowDown size={12} />
                    </button>
                  </div>
                  <button
                    onClick={() => removeUE(idx)}
                    className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    title="Supprimer cette UE"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {/* Matières de l'UE */}
              {ue.matieres.length === 0 ? (
                <div className="py-6 text-center text-sm text-gray-400 italic border-2 border-dashed border-gray-200 dark:border-gray-700 rounded">
                  {ue.est_tp ? 'UE TP — vide, prête à recevoir les travaux pratiques' : 'Aucune matière. Sélectionnez-en une ailleurs puis cliquez "Déplacer ici".'}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {ue.matieres.map(m => (
                    <button
                      key={m.id}
                      onClick={() => setSelection({ ueIdx: idx, mid: m.id })}
                      className={`px-3 py-1.5 rounded-lg text-xs border transition-colors flex items-center gap-1 ${
                        selection?.mid === m.id && selection?.ueIdx === idx
                          ? 'bg-blue-600 text-white border-blue-700'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/30'
                      }`}
                    >
                      <span className="font-medium">{m.nom_matiere}</span>
                      <span className="opacity-70">· {m.credits} cr</span>
                      <span className="text-[9px] opacity-60 uppercase">{m.categorie}</span>
                      {(m as any).type_enseignement && (
                        <span className={`text-[9px] uppercase px-1 py-0 rounded font-medium ${typeBadgeClass((m as any).type_enseignement)}`}>
                          {(m as any).type_enseignement?.substring(0, 5)}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </Card>
          ))}

          {/* Bouton ajouter UE */}
          <button
            onClick={addUE}
            className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center gap-2 text-sm"
          >
            <Plus size={16} /> Ajouter une UE
          </button>

          {/* Action "déplacer vers orphelines" quand sélection active dans une UE */}
          {selection && selection.ueIdx !== 'orphans' && (
            <div className="fixed bottom-4 right-4 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-3 flex items-center gap-3 z-20">
              <span className="text-sm text-gray-600 dark:text-gray-300">Matière sélectionnée</span>
              <Button
                variant="secondary"
                size="sm"
                icon={<ArrowLeft size={14} />}
                onClick={() => moveMatiere(selection.mid, selection.ueIdx, 'orphans')}
              >
                Détacher
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelection(null)}
              >
                Annuler
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Modal copie vers d'autres options */}
      <CopierVersOptionsModal
        open={showCopierModal}
        onClose={() => setShowCopierModal(false)}
        optionSource={selectedOption ? { id: selectedOption.id, nom: selectedOption.nom } : null}
        niveau={niveau}
        semestre={semestre}
        optionsCandidates={(options as any[]).filter(o => o.id !== selectedOption?.id)}
      />

      {/* Modal : proposer un recalcul CS après modification d'une option CS */}
      {recalcCSPropose && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
             onClick={() => !isRecalculatingCS && setRecalcCSPropose(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <AlertTriangle size={16} className="text-amber-600" />
                Recalcul des moyennes CS recommandé
              </h3>
              <button onClick={() => setRecalcCSPropose(null)} disabled={isRecalculatingCS}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded disabled:opacity-40">
                <XCircle size={16} />
              </button>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-800 dark:text-gray-200">
              <p>
                Vous venez de déplacer <strong>{recalcCSPropose.matieresDeplacees} matière{recalcCSPropose.matieresDeplacees > 1 ? 's' : ''}</strong>
                {' '}dans les UE de l'option <strong>{recalcCSPropose.optionNom}</strong>.
              </p>
              <p>
                Comme la moyenne semestre des étudiants CS = moyenne simple des moyennes UE,
                il faut relancer le recalcul CS pour que le Tableau de Délibération reflète
                la nouvelle structure.
              </p>
              <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded p-2 text-xs">
                Le recalcul cible : <strong>{niveau} · S{((semestre - 1) % 2) + 1}</strong>
                {' · '}<strong>option {recalcCSPropose.optionNom}</strong>. Aucun impact sur FI/DD.
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button onClick={() => setRecalcCSPropose(null)} disabled={isRecalculatingCS}
                        className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded disabled:opacity-40">
                  Plus tard
                </button>
                <button onClick={handleRecalculerCSFromModal} disabled={isRecalculatingCS}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-700 hover:bg-indigo-800 disabled:opacity-60 text-white text-xs font-semibold rounded">
                  {isRecalculatingCS ? <Loader size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  {isRecalculatingCS ? 'Recalcul en cours…' : 'Lancer le recalcul CS'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RepartitionUE;
